import { useApiClient } from "@/lib/api/api.client";
import type { Permission } from "@soulbyte/core";
import { useIsEntitled } from "@/lib/auth/permissions/permissions";
import { useAuth } from "@frontegg/react";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useInvalidateConversations } from "./conversations.api";
import { sortThreadsByRecency, type Thread } from "./threads.utils";

// =============================================================================
// /threads/{assistant_id}/{user_id}
// =============================================================================

export function useThreads({
  assistantId,
  userId,
}: {
  assistantId: string;
  userId: string;
}) {
  const apiClient = useApiClient();
  const isEntitled = useIsEntitled("thread.list");

  const query = apiClient.useQuery(
    "get",
    "/threads/{assistant_id}/{user_id}",
    {
      params: {
        path: {
          assistant_id: assistantId,
          user_id: userId,
        },
      },
    },
    {
      enabled: isEntitled && !!userId && !!assistantId,
    },
  );

  const _threads = query.data?.data?.threads;
  const threads = useMemo(
    () =>
      _threads?.length ? sortThreadsByRecency(_threads) : (_threads ?? []),
    [_threads],
  );

  return Object.assign({}, query, {
    threads,
    isEntitled,
    permissions: ["thread.list"] satisfies Permission[],
  });
}

// Fetches one thread's details (GET /threads/{assistant_id}/details/{thread_id}).
// Used by the chat screen to know a thread's `source` so website/phone
// conversations render read-only.
export function useThread({
  assistantId,
  threadId,
}: {
  assistantId: string;
  threadId: string;
}) {
  const apiClient = useApiClient();

  const query = apiClient.useQuery(
    "get",
    "/threads/{assistant_id}/details/{thread_id}",
    {
      params: {
        path: { assistant_id: assistantId, thread_id: threadId },
      },
    },
    { enabled: !!assistantId && !!threadId },
  );

  return Object.assign({}, query, { thread: query.data?.data?.thread });
}

export function useInvalidateThreads() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return function ({
    assistantId,
    userId,
  }: {
    assistantId: string;
    userId: string;
  }) {
    const queryOptions = apiClient.queryOptions(
      "get",
      "/threads/{assistant_id}/{user_id}",
      {
        params: {
          path: {
            assistant_id: assistantId,
            user_id: userId,
          },
        },
      },
    );

    return queryClient.invalidateQueries(queryOptions);
  };
}

// =============================================================================
// Create Thread
// =============================================================================

export function useCreateThread({
  onSuccess,
}: {
  onSuccess?: (thread: Thread) => void | Promise<void>;
} = {}) {
  const { user } = useAuth();
  const apiClient = useApiClient();

  const isEntitled = useIsEntitled("thread.add");
  const invalidateThreads = useInvalidateThreads();
  const invalidateConversations = useInvalidateConversations();

  const mutation = apiClient.useMutation("post", "/threads/{assistant_id}", {
    onSuccess: async (data, variables) => {
      const userId = variables.body?.user_id ?? user?.id;
      const assistantId = variables.params.path.assistant_id;

      if (userId && assistantId) {
        await invalidateThreads({
          userId,
          assistantId,
        });
      }
      // The chat thread list is driven by the conversations query.
      if (assistantId) await invalidateConversations({ assistantId });

      const thread = data?.data?.thread;
      if (thread) await onSuccess?.(thread);
    },
  });

  return Object.assign({}, mutation, {
    isEntitled,
    permissions: ["thread.add"],
  });
}

// =============================================================================
// Delete Thread
// =============================================================================

export function useDeleteThread({
  onSuccess,
}: {
  onSuccess?: () => void | Promise<void>;
}) {
  const { user } = useAuth();
  const apiClient = useApiClient();

  const isEntitled = useIsEntitled("thread.delete");
  const invalidateThreads = useInvalidateThreads();
  const invalidateConversations = useInvalidateConversations();

  const mutation = apiClient.useMutation(
    "delete",
    "/threads/{assistant_id}/details/{thread_id}",
    {
      onSuccess: async (_data, variables) => {
        const userId = user?.id;
        const assistantId = variables.params.path.assistant_id;

        if (userId && assistantId) {
          await invalidateThreads({
            userId,
            assistantId,
          });
        }
        if (assistantId) await invalidateConversations({ assistantId });

        await onSuccess?.();
      },
    },
  );

  return Object.assign({}, mutation, {
    isEntitled,
    permissions: ["thread.delete"],
  });
}
